import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api.js';
import { t, huidigeTaal } from '../i18n.js';
import Vlag from '../components/Vlag.jsx';
import { landnaam } from '../components/Wereldkaart.jsx';

/**
 * Beheer › Detectie: wat een beheerder instelt (de landen waar een VPN-login normaal is), en welke
 * drempels er gelden. De drempels staan in de code (src/modules/detectie/vpn.js); zie
 * docs/besluiten/vpn-detecties.md.
 */
export default function BeheerDetectie() {
  const client = useQueryClient();
  const taal = huidigeTaal();
  const q = useQuery({ queryKey: ['beheer-detectie'], queryFn: api.beheerDetectie });
  const [landen, zetLanden] = useState(null);
  const [nieuw, zetNieuw] = useState('');
  const [melding, zetMelding] = useState(null);
  useEffect(() => { if (q.data && !landen) zetLanden(q.data.data.vpnLanden); }, [q.data, landen]);
  if (q.isPending || !landen) return <p className="muted">{t('Laden…')}</p>;
  if (q.isError) return <div className="melder rood">{t(q.error.message)}</div>;
  const r = q.data.data.rules;

  const voegToe = () => {
    const code = nieuw.trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(code)) { zetMelding({ goed: false, tekst: t('Een landcode van twee letters') }); return; }
    zetLanden((l) => [...new Set([...l, code])].sort());
    zetNieuw('');
    zetMelding(null);
  };
  const bewaar = async () => {
    zetMelding(null);
    try {
      await api.zetBeheerDetectie({ vpnLanden: landen });
      zetMelding({ goed: true, tekst: t('Opgeslagen.') });
      await client.invalidateQueries({ queryKey: ['beheer-detectie'] });
    } catch (e) {
      zetMelding({ goed: false, tekst: t(e.details ? Object.values(e.details)[0] : e.message) });
    }
  };

  return (
    <>
      {melding && <div className={`melder ${melding.goed ? 'blauw' : 'rood'}`} style={{ marginBottom: 14 }}>{melding.tekst}</div>}
      <section className="card setblok">
        <span className="label">{t('VPN')}</span>
        <div className="setrij">
          <div className="setkop"><b>{t('Landen waar een VPN-login normaal is')}</b>
            <span>{t('Een geslaagde login van buiten deze landen geeft een alert met ernst hoog. Interne adressen en adressen zonder bekend land tellen niet.')}</span></div>
          <div className="form" style={{ gap: 10 }}>
            <div className="filters" style={{ margin: 0 }}>
              {landen.map((c) => (
                <span key={c} className="chip" aria-pressed="true"><Vlag cc={c} />{landnaam(c, taal) ?? c} <span className="tel">{c}</span>
                  <button type="button" className="namelink" style={{ marginLeft: 6 }} aria-label={t('{land} weghalen', { land: landnaam(c, taal) ?? c })}
                    disabled={landen.length === 1} onClick={() => zetLanden((l) => l.filter((x) => x !== c))}>×</button></span>
              ))}
            </div>
            <form className="themarij" style={{ gap: 8 }} onSubmit={(e) => { e.preventDefault(); voegToe(); }}>
              <input className="input" value={nieuw} onChange={(e) => zetNieuw(e.target.value)} maxLength={2} style={{ width: 80, textTransform: 'uppercase' }}
                placeholder="FR" aria-label={t('Landcode')} />
              <button className="btn" disabled={!nieuw.trim()}>{t('Toevoegen')}</button>
              {nieuw.trim().length === 2 && <span className="muted"><Vlag cc={nieuw.trim()} />{landnaam(nieuw.trim().toUpperCase(), taal)}</span>}
            </form>
            <div><button type="button" className="btn primary" onClick={bewaar}>{t('Opslaan')}</button></div>
          </div>
        </div>
        <div className="setrij">
          <div className="setkop"><b>{t('Drempels')}</b><span>{t('Vast in ADTS ZERO; een andere drempel is een wijziging in de code, met een test.')}</span></div>
          <ul className="soft" style={{ margin: 0, paddingLeft: 18, lineHeight: 1.7 }}>
            <li>{t('Brute force: {n} mislukte logins vanaf één adres binnen {m} minuten (hoog)', { n: r.bruteForce.pogingen, m: r.bruteForce.minuten })}</li>
            <li>{t('Password spraying: {n} of meer verschillende gebruikersnamen vanaf één adres (hoog)', { n: r.spraying.namen })}</li>
            <li>{t('Geslaagde login na minstens {n} mislukte vanaf hetzelfde adres in {m} minuten (kritiek, ook buiten kantooruren naar Webex)', { n: r.naMislukt.pogingen, m: r.naMislukt.minuten })}</li>
            <li>{t('Geslaagde login uit een ander land of vanaf een hostingprovider (hoog)')}</li>
            <li>{t('Een aanval zonder mislukte login in {m} minuten heet gestopt; het alert blijft open', { m: r.gestoptNa })}</li>
          </ul>
        </div>
        <div className="callout" style={{ marginTop: 12 }}>
          {t('De firewall schrijft de gebruikersnaam van een mislukte login standaard als *****. Password spraying gaat pas af als een engineer dat op de firewall uitzet (no logging hide username). Dan staan ook verkeerd getypte wachtwoorden in de log.')}
        </div>
      </section>
    </>
  );
}
